import React from 'react';
import { logMapError } from '../../utils/mapDiagnostics';

type Props = {
  /** Name used in the log line, e.g. `HomeFeedMap`. */
  mapName: string;
  /** Called once when the subtree crashes, so the parent can show its fallback. */
  onError: () => void;
  children: React.ReactNode;
};

type State = { hasError: boolean };

/**
 * Contains a render crash inside a map to the map itself. Without it the
 * throw reaches AppErrorBoundary and the whole app swaps to SnagScreen.
 * Renders nothing on error; the parent owns the fallback UI. Reset by
 * changing this component's `key`.
 */
export class MapErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    try {
      logMapError(this.props.mapName, 'render', error, {
        componentStack: info.componentStack ?? '',
      });
    } catch {
      // logger itself may be the thing that's broken — swallow
    }
    this.props.onError();
  }

  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}
